/**
 * Client-side CV file text extraction.
 *
 * Supports PDF (pdfjs-dist), DOCX (mammoth), and plain text (.txt / .md).
 * Returns the extracted raw text + detected kind. The caller then sends this
 * text to /api/ai?route=parse for AI-powered structured parsing.
 *
 * Why CDN imports?
 *   `build.ts` marks `pdfjs-dist` and `mammoth` as external (they're heavy
 *   Node/browser libs that trip the bundler), and nginx serves the built
 *   `dist/` in production, so bare-specifier imports resolve to nothing at
 *   runtime ("Failed to resolve module specifier 'pdfjs-dist'"). Loading
 *   from jsdelivr's ESM CDN bypasses the bundler entirely, keeps the main
 *   bundle small, and the libs are fetched lazily only when the user
 *   actually opens the wizard Import step. The CSP already whitelists
 *   `cdn.jsdelivr.net` for scripts.
 *
 *   Bun's HTML bundler leaves URL specifiers in dynamic imports untouched,
 *   so `await import('https://…')` resolves at runtime via the browser's
 *   native ESM loader.
 */

// Pinned versions — bump together with package.json to stay in sync.
const PDFJS_VERSION = '5.4.530';
const PDFJS_CDN = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/+esm`;
const PDFJS_WORKER_CDN = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;
const MAMMOTH_CDN = 'https://cdn.jsdelivr.net/npm/mammoth@1.12.0/mammoth.browser.min.js';

interface PdfJsModule {
  getDocument(src: { data: Uint8Array; useWorkerFetch?: boolean; isEvalSupported?: boolean; useSystemFonts?: boolean; disableFontFace?: boolean }): { promise: Promise<PdfDocument> };
  GlobalWorkerOptions: { workerSrc: string };
}
interface PdfDocument {
  numPages: number;
  getPage(n: number): Promise<PdfPage>;
}
interface PdfPage {
  getTextContent(): Promise<{ items: Array<{ str?: string; transform?: number[]; hasEOL?: boolean }> }>;
}

let pdfjsPromise: Promise<PdfJsModule> | null = null;
function loadPdfJs(): Promise<PdfJsModule> {
  if (!pdfjsPromise) {
    pdfjsPromise = (async () => {
      const lib = (await import(/* @vite-ignore */ PDFJS_CDN)) as PdfJsModule;
      // pdfjs v5 requires a worker. We fetch the worker script from jsdelivr
      // (allowed by `connect-src cdn.jsdelivr.net` in the nginx CSP), wrap it
      // in a Blob, and install the blob URL as `workerSrc` — which is allowed
      // by `worker-src 'self' blob:`.
      try {
        const resp = await fetch(PDFJS_WORKER_CDN);
        if (!resp.ok) throw new Error(`worker fetch failed: ${resp.status}`);
        const workerSrc = await resp.text();
        const blob = new Blob([workerSrc], { type: 'application/javascript' });
        lib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
      } catch (err) {
        // Last resort: let pdfjs try to load the worker directly from the CDN URL.
        // Requires `worker-src cdn.jsdelivr.net` in the CSP — not currently the case,
        // so this will usually fail, but at least gives a clearer error than "undefined".
        console.warn('pdfjs worker blob install failed, falling back to direct CDN worker', err);
        lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
      }
      return lib;
    })();
  }
  return pdfjsPromise;
}

interface MammothModule {
  extractRawText(input: { arrayBuffer: ArrayBuffer }): Promise<{ value: string }>;
}
let mammothPromise: Promise<MammothModule> | null = null;
function loadMammoth(): Promise<MammothModule> {
  if (!mammothPromise) {
    // mammoth doesn't publish a proper ESM build on jsdelivr /+esm, so we
    // load the UMD browser bundle via <script> and read from window.
    mammothPromise = new Promise<MammothModule>((resolve, reject) => {
      const existing = (globalThis as unknown as { mammoth?: MammothModule }).mammoth;
      if (existing) { resolve(existing); return; }
      const s = document.createElement('script');
      s.src = MAMMOTH_CDN;
      s.async = true;
      s.onload = () => {
        const m = (globalThis as unknown as { mammoth?: MammothModule }).mammoth;
        if (m) resolve(m);
        else reject(new Error('mammoth global not found after load'));
      };
      s.onerror = () => reject(new Error('Failed to load mammoth from CDN'));
      document.head.appendChild(s);
    });
  }
  return mammothPromise;
}

export type CvFileKind = 'pdf' | 'docx' | 'text' | 'json';

export interface ExtractedCvFile {
  /** Raw extracted text (empty string for JSON backups). */
  text: string;
  /** Detected file kind. */
  kind: CvFileKind;
  /** For JSON backups, the parsed JSON payload (CVData or { data: CVData }). */
  json?: unknown;
}

/** Detect file kind from MIME + extension. */
function detectKind(file: File): CvFileKind | null {
  const name = file.name.toLowerCase();
  const mime = file.type;
  if (mime === 'application/pdf' || name.endsWith('.pdf')) return 'pdf';
  if (
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    name.endsWith('.docx')
  ) return 'docx';
  if (name.endsWith('.json') || mime === 'application/json') return 'json';
  if (name.endsWith('.txt') || name.endsWith('.md') || name.endsWith('.text') || mime.startsWith('text/')) return 'text';
  return null;
}

/**
 * Extract text from a PDF using pdfjs-dist. Preserves reading order by
 * sorting items on each page by their vertical then horizontal position,
 * and inserts a newline between lines whose y-delta exceeds a threshold.
 * This gives a much cleaner input to the AI parser than the naive
 * `.map(i => i.str).join(' ')` used in useCvFileAnalysis.
 */
async function extractPdfText(arrayBuffer: ArrayBuffer): Promise<string> {
  const pdfjsLib = await loadPdfJs();
  // workerSrc is installed inside loadPdfJs() as a blob URL fetched from jsdelivr.

  const loadingTask = pdfjsLib.getDocument({
    data: new Uint8Array(arrayBuffer),
    useWorkerFetch: false,
    isEvalSupported: false,
    useSystemFonts: true,
    disableFontFace: true,
  });

  const pdf = await loadingTask.promise;
  const pages: string[] = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();

    type Item = { str?: string; transform?: number[]; hasEOL?: boolean };
    const items = content.items as Item[];

    // Group items into lines by their y-position (transform[5]).
    const lines: Array<{ y: number; x: number; str: string }> = [];
    let currentLine: { y: number; items: Array<{ x: number; str: string }> } | null = null;
    const Y_TOLERANCE = 3; // pixels

    for (const it of items) {
      const str = it.str ?? '';
      if (!str && !it.hasEOL) continue;
      const x = it.transform?.[4] ?? 0;
      const y = it.transform?.[5] ?? 0;

      if (!currentLine || Math.abs(currentLine.y - y) > Y_TOLERANCE) {
        if (currentLine) {
          currentLine.items.sort((a, b) => a.x - b.x);
          lines.push({ y: currentLine.y, x: currentLine.items[0]?.x ?? 0, str: currentLine.items.map(i => i.str).join(' ') });
        }
        currentLine = { y, items: str ? [{ x, str }] : [] };
      } else if (str) {
        currentLine.items.push({ x, str });
      }
      if (it.hasEOL && currentLine) {
        currentLine.items.sort((a, b) => a.x - b.x);
        lines.push({ y: currentLine.y, x: currentLine.items[0]?.x ?? 0, str: currentLine.items.map(i => i.str).join(' ') });
        currentLine = null;
      }
    }
    if (currentLine) {
      currentLine.items.sort((a, b) => a.x - b.x);
      lines.push({ y: currentLine.y, x: currentLine.items[0]?.x ?? 0, str: currentLine.items.map(i => i.str).join(' ') });
    }

    // In PDF coords, larger y = higher on page, so sort descending.
    lines.sort((a, b) => b.y - a.y);

    const pageText = lines
      .map(l => l.str.replace(/\s+/g, ' ').trim())
      .filter(Boolean)
      .join('\n');

    pages.push(pageText);
  }

  return pages.join('\n\n').trim();
}

async function extractDocxText(arrayBuffer: ArrayBuffer): Promise<string> {
  const mammoth = await loadMammoth();
  const result = await mammoth.extractRawText({ arrayBuffer });
  return (result.value ?? '').trim();
}

/**
 * Main entrypoint: detects the file kind and extracts text accordingly.
 * Throws on unsupported formats or parse errors.
 */
export async function extractCvFile(file: File): Promise<ExtractedCvFile> {
  const kind = detectKind(file);
  if (!kind) {
    throw new Error('Format non supporté. Utilisez PDF, DOCX, TXT ou un backup JSON ATSmyCV.');
  }

  if (kind === 'json') {
    const raw = await file.text();
    try {
      const json = JSON.parse(raw);
      return { text: '', kind, json };
    } catch {
      throw new Error('Fichier JSON invalide.');
    }
  }

  if (kind === 'text') {
    const text = (await file.text()).trim();
    if (!text) throw new Error('Le fichier est vide.');
    return { text, kind };
  }

  const arrayBuffer = await file.arrayBuffer();
  const text = kind === 'pdf'
    ? await extractPdfText(arrayBuffer)
    : await extractDocxText(arrayBuffer);

  if (!text.trim()) {
    throw new Error(
      kind === 'pdf'
        ? 'Aucun texte extrait du PDF. Le document est peut-être une image scannée — essaie de copier-coller le texte.'
        : 'Aucun texte extrait du DOCX.'
    );
  }

  return { text, kind };
}
